import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import SEO from '../../components/seo';
import PropertyGrid from '../property-search-browse/components/PropertyGrid';
import Icon from '../../components/AppIcon';
import {
  useGetCategoriesQuery,
  useGetPropertiesQuery,
  useGetSeoMarketsQuery,
  useGetUserFavouritePropertyQuery,
} from '../../features/api/apiSlice';
import { selectIsAuthenticated } from '../../features/auth/authSlice';
import {
  FALLBACK_MARKETS,
  MARKET_INTENTS,
  SITE_URL,
  cityFromSlug,
  citySlug,
} from '../../lib/seo';
import { isLandCategory } from '../../lib/propertyLabels';

const isLandNameSafe = (name) => {
  try {
    return isLandCategory({ category: name });
  } catch {
    return /\b(land|plot)\b/i.test(String(name || ''));
  }
};

const MarketPage = ({ intent: intentProp }) => {
  const { city: cityParam } = useParams();
  const intent = intentProp || 'for-rent';
  const config = MARKET_INTENTS[intent] || MARKET_INTENTS['for-rent'];
  const city = cityParam ? cityFromSlug(cityParam) : '';
  const [page, setPage] = useState(1);
  const pageSize = 24;

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { data: userFavoriteProperties = [] } = useGetUserFavouritePropertyQuery(undefined, {
    skip: !isAuthenticated,
  });
  const favoritePropertyIds = useMemo(
    () => (userFavoriteProperties || []).map((fav) => fav.property_id || fav.id).filter(Boolean),
    [userFavoriteProperties]
  );

  const { data: categories = [] } = useGetCategoriesQuery();
  const landCategory = useMemo(
    () => categories.find((cat) => isLandNameSafe(cat?.category)),
    [categories]
  );
  const { data: markets = [] } = useGetSeoMarketsQuery();
  const cityLinks = useMemo(() => {
    if (markets.length) {
      return markets
        .filter((m) => {
          if (intent === 'for-rent') return m.rent_count > 0;
          if (intent === 'land') return m.land_count > 0;
          return m.sale_count > 0;
        })
        .slice(0, 16);
    }
    return FALLBACK_MARKETS.map((name) => ({ city: name, slug: citySlug(name) }));
  }, [markets, intent]);

  const queryParams = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    property_type: config.propertyType,
    city: city || undefined,
    category_id: intent === 'land' && landCategory?.id ? landCategory.id : undefined,
  };

  const { data, isLoading, isFetching } = useGetPropertiesQuery(queryParams);
  const properties = data?.data || [];
  const totalResults = data?.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));

  const placeLabel = city || 'Uganda';
  const title = config.title(placeLabel);
  const description = config.description(placeLabel);
  const canonical = city
    ? `${SITE_URL}/${intent}/${citySlug(city)}`
    : `${SITE_URL}/${intent}`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: canonical,
    about: {
      '@type': 'Place',
      name: placeLabel,
      address: { '@type': 'PostalAddress', addressLocality: placeLabel, addressCountry: 'UG' },
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: totalResults,
      itemListElement: properties.slice(0, 20).map((property, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}/property/${property.slug_id}`,
        name: property.title,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={title}
        description={description}
        keywords={config.keywords(placeLabel)}
        canonicalUrl={canonical}
        schema={schema}
      />
      <Header />
      <main className="pt-16">
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
              HireBroker listings · {placeLabel}
            </p>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              {config.h1(placeLabel)}
            </h1>
            <p className="mt-3 max-w-3xl text-slate-600 leading-relaxed">{description}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {Object.entries(MARKET_INTENTS).map(([key, value]) => (
                <Link
                  key={key}
                  to={city ? `/${key}/${citySlug(city)}` : `/${key}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                    key === intent
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {value.label}
                </Link>
              ))}
              <Link
                to="/property-search-browse"
                className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 text-slate-700 hover:border-slate-400"
              >
                All listings
              </Link>
            </div>
          </div>
        </section>

        {cityLinks.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Browse by city</h2>
            <div className="flex flex-wrap gap-2">
              {cityLinks.map((market) => (
                <Link
                  key={market.slug || market.city}
                  to={`/${intent}/${market.slug || citySlug(market.city)}`}
                  className="px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                >
                  {market.city}
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-slate-500">
              {isLoading ? 'Loading listings…' : `${totalResults} verified listing${totalResults === 1 ? '' : 's'}`}
            </p>
          </div>
          <PropertyGrid
            properties={properties}
            loading={isLoading || isFetching}
            favoritePropertyIds={favoritePropertyIds}
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalResults}
            resultsPerPage={pageSize}
            onPageChange={setPage}
          />
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center flex-shrink-0">
                <Icon name="ShieldCheck" size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">
                  Why search {config.label.toLowerCase()} on HireBroker?
                </h2>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  HireBroker lists verified {config.label.toLowerCase()} inventory in {placeLabel} with photos,
                  prices in UGX, and credential-checked agents. Post a specific property request if you need a
                  match that is not on the market yet.
                </p>
                <Link
                  to="/notice-board"
                  className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-slate-900 hover:underline"
                >
                  Post a Specific Property Request
                  <Icon name="ArrowRight" size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MarketPage;
